import * as Sentry from "@sentry/react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import React, { Component, ErrorInfo, ReactNode } from "react";

import { Button } from "./ui/button.js";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global Class-Based Error Boundary
 *
 * This catches errors that occur in the React component tree
 * (not caught by the router-based ErrorBoundary).
 *
 * Features:
 * - Hides technical details from users in production
 * - Logs full error details to console for developers
 * - Provides recovery actions
 * - Shows collapsible technical details in development mode
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details to console for developers
    console.error("=".repeat(80));
    console.error("GLOBAL ERROR BOUNDARY CAUGHT AN ERROR");
    console.error("=".repeat(80));
    console.error("Error:", error);
    console.error("Error Message:", error.message);
    console.error("Stack Trace:", error.stack);
    console.error("Component Stack:", errorInfo.componentStack);
    console.error("=".repeat(80));

    // Report to Sentry. React render errors are caught here by the boundary,
    // not by the global handler, so we must capture explicitly. No-op when
    // Sentry isn't initialized (DSN unset).
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDev = import.meta.env.DEV;

      // User-friendly error message (no technical details)
      const title = "Something went wrong";
      const userMessage = "We encountered an unexpected error while displaying this page. Please try reloading or returning to the home page.";

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-background to-muted/20 px-4">
          <div className="max-w-md w-full">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="rounded-full bg-destructive/10 p-6">
                <AlertTriangle className="h-12 w-12 text-destructive" />
              </div>
            </div>

            {/* Error Card */}
            <div className="bg-card rounded-lg shadow-xl border border-border p-8 text-center">
              {/* Title */}
              <h1 className="text-2xl font-bold text-foreground mb-3">
                {title}
              </h1>

              {/* User-friendly message */}
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {userMessage}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleReload}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload Page
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Go to Home
                </Button>
              </div>

              {/* Try Again Button - attempts to recover without reload */}
              <div className="mt-3">
                <Button
                  onClick={this.handleReset}
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                >
                  Try to recover without reloading
                </Button>
              </div>

              {/* Development Mode: Technical Details */}
              {isDev && error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground mb-2">
                    Technical Details (Development Only)
                  </summary>
                  <div className="bg-muted rounded-md p-4 overflow-x-auto max-h-96 overflow-y-auto">
                    <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                      <div className="font-bold mb-2">Error Message:</div>
                      <div className="mb-4 text-destructive">{error.message}</div>

                      {error.stack && (
                        <>
                          <div className="font-bold mb-2">Stack Trace:</div>
                          <div className="mb-4">{error.stack}</div>
                        </>
                      )}

                      {errorInfo?.componentStack && (
                        <>
                          <div className="font-bold mb-2">Component Stack:</div>
                          <div>{errorInfo.componentStack}</div>
                        </>
                      )}
                    </pre>
                  </div>
                </details>
              )}
            </div>

            {/* Help Text */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
